/**
 * SHER-1128 (SHER-308 Phase 4) — price-list grid model.
 *
 * The price tab keeps today's billables ReactGrid verbatim (fixed columns, delete toggle,
 * add-row footer, column widths) and only swaps the data columns: one column per active
 * PriceList code instead of one per insurer. These pure builders own the column/row shape
 * so the component stays a thin ReactGrid wrapper and the structure can be pinned by a unit
 * test (ReactGrid does not render meaningfully under jsdom).
 *
 * Decision C (David, 2026-10-20): price columns are the active lists, editable in-cell, and
 * adding a new list = adding a column — so a trailing add-list column mirrors the add-row
 * footer the grid already has.
 */
import { Column, Row } from '@silevis/reactgrid';
import { parseCsvLine, escapeCsvField } from './billingCsv';
import { ACTION_COLUMN_ID, HEADER_ROW_ID, ADD_ROW_FOOTER_ID } from './editableGrid';

export { ACTION_COLUMN_ID };
export const ADD_LIST_COLUMN_ID = '__add_list__';

/** The fixed data columns, identical to the billables grid today (after the action column). */
export const FIXED_DATA_COLUMN_IDS = [
    'serviceType',
    'category',
    'serviceCode',
    'department',
    'name',
    'billableType',
    'available',
] as const;

const ACTION_COLUMN_WIDTH = 80;
const ADD_LIST_COLUMN_WIDTH = 120;

const DEFAULT_FIXED_WIDTHS: Record<string, number> = {
    [ACTION_COLUMN_ID]: ACTION_COLUMN_WIDTH,
    serviceType: 120,
    category: 150,
    serviceCode: 120,
    department: 120,
    name: 250,
    billableType: 120,
    available: 100,
};

/** Default width for a list column based on its code length (min 100, max 250) — same rule insurers used. */
export function getDefaultListColumnWidth(code: string): number {
    const base = 60 + (code?.trim().length ?? 0) * 7;
    return Math.min(250, Math.max(100, base));
}

export interface PriceGridRow {
    action?: '' | 'DELETE';
    serviceType: string;
    category: string;
    serviceCode: string;
    department: string;
    name: string;
    billableType: string;
    available: string;
    /**
     * price per PriceList code. SHER-1128: a list absent here has NO price on that list (the
     * service falls back to STD) and stays an empty cell end to end; it is never 0, which would
     * be saved as a real price of 0.
     */
    prices: { [listCode: string]: number };
}

/**
 * Apply an edited numeric cell to a row's (already copied) prices. An empty or cleared cell —
 * ReactGrid hands it back as NaN — removes the price; any number, including a typed 0, sets it.
 */
export function setCellPrice(prices: PriceGridRow['prices'], listCode: string, cellValue: number): void {
    if (Number.isNaN(cellValue)) delete prices[listCode];
    else prices[listCode] = cellValue;
}

/**
 * Columns: the action column, the seven fixed billable columns, one column per list code,
 * and a trailing add-list column.
 */
export function buildPriceGridColumns(
    listCodes: string[],
    widths: Record<string, number> = {},
): Column[] {
    const width = (id: string, fallback: number) => widths[id] ?? DEFAULT_FIXED_WIDTHS[id] ?? fallback;

    const fixedColumns: Column[] = [
        { columnId: ACTION_COLUMN_ID, width: width(ACTION_COLUMN_ID, ACTION_COLUMN_WIDTH), resizable: false },
        ...FIXED_DATA_COLUMN_IDS.map((id) => ({ columnId: id, width: width(id, 120), resizable: true })),
    ];

    const listColumns: Column[] = listCodes.map((code) => ({
        columnId: code,
        width: widths[code] ?? getDefaultListColumnWidth(code),
        resizable: true,
    }));

    const addListColumn: Column = { columnId: ADD_LIST_COLUMN_ID, width: ADD_LIST_COLUMN_WIDTH, resizable: false };

    return [...fixedColumns, ...listColumns, addListColumn];
}

/**
 * Rows: a sticky header (fixed headers + list codes + an add-list "+" header), one data row
 * per billable (delete rows flagged red, list prices as numeric cells), and the add-row footer.
 */
export function buildPriceGridRows(rows: PriceGridRow[], listCodes: string[]): Row[] {
    const headerRow: Row = {
        rowId: HEADER_ROW_ID,
        cells: [
            { type: 'header', text: 'Action' },
            { type: 'header', text: 'Service Type' },
            { type: 'header', text: 'Category' },
            { type: 'header', text: 'Service Code' },
            { type: 'header', text: 'Department' },
            { type: 'header', text: 'Name' },
            { type: 'header', text: 'Billable Type' },
            { type: 'header', text: 'Available' },
            ...listCodes.map((code) => ({ type: 'header' as const, text: code })),
            { type: 'header', text: '➕ List' },
        ],
    };

    const dataRows: Row[] = rows.map((row, index) => {
        const isDeleted = row.action === 'DELETE';
        const cls = isDeleted ? 'deleted-row' : '';
        return {
            rowId: index,
            cells: [
                { type: 'text', text: isDeleted ? '❌' : '🗑️', nonEditable: false, className: cls },
                { type: 'text', text: row.serviceType, nonEditable: false, className: cls },
                { type: 'text', text: row.category, className: cls },
                { type: 'text', text: row.serviceCode, className: cls },
                { type: 'text', text: row.department, className: cls },
                { type: 'text', text: row.name, className: cls },
                { type: 'text', text: row.billableType, className: cls },
                { type: 'text', text: row.available, className: cls },
                ...listCodes.map((code) => ({
                    type: 'number' as const,
                    // NaN renders as an empty cell in ReactGrid: the row has no price on this list.
                    value: row.prices[code] ?? NaN,
                    className: cls,
                })),
                { type: 'text' as const, text: '', nonEditable: true },
            ],
        };
    });

    const footerRow: Row = {
        rowId: ADD_ROW_FOOTER_ID,
        cells: [
            { type: 'text', text: '➕', nonEditable: false, className: 'add-row-cell' },
            { type: 'text', text: 'Click + to add a new row', nonEditable: true, className: 'add-row-hint' },
            // fillers across the remaining fixed columns (category…available) and the list columns.
            ...Array(6 + listCodes.length).fill(null).map(() => ({
                type: 'text' as const,
                text: '',
                nonEditable: true,
            })),
            // the add-list column's footer cell is the clickable "new list = new column" affordance.
            { type: 'text', text: '➕ list', nonEditable: false, className: 'add-row-cell' },
        ],
    };

    return [headerRow, ...dataRows, footerRow];
}

/** action + the 7 fixed billable columns, before the list columns. */
export const FIXED_COLUMNS_COUNT = 1 + FIXED_DATA_COLUMN_IDS.length;

/**
 * Parse a price-lists.csv (the per-list billables export) into list codes + rows. The data
 * columns after the fixed billable columns are the list codes; an empty cell means the row has
 * no price on that list and is left out of `prices` (SHER-1128).
 */
export function parsePriceCsv(csvContent: string): { lists: string[]; rows: PriceGridRow[] } {
    const lines = csvContent.trim().split('\n');
    if (lines.length === 0) return { lists: [], rows: [] };

    const headers = parseCsvLine(lines[0]);
    const hasAction = headers[0]?.toLowerCase() === 'action';
    const dataStart = hasAction ? 1 : 0;
    const listStart = hasAction ? FIXED_COLUMNS_COUNT : FIXED_COLUMNS_COUNT - 1;
    const lists = headers.slice(listStart);

    if (lines.length === 1) return { lists, rows: [] };

    const rows: PriceGridRow[] = lines.slice(1).map(line => {
        const values = parseCsvLine(line);
        const prices: { [code: string]: number } = {};
        lists.forEach((code, i) => {
            const price = parseInt(values[listStart + i] ?? '');
            if (!Number.isNaN(price)) prices[code] = price;
        });
        const actionValue = hasAction ? values[0] : '';
        return {
            action: actionValue?.toUpperCase() === 'DELETE' ? 'DELETE' : '',
            serviceType: values[dataStart + 0] || '',
            category: values[dataStart + 1] || '',
            serviceCode: values[dataStart + 2] || '',
            department: values[dataStart + 3] || '',
            name: values[dataStart + 4] || '',
            billableType: values[dataStart + 5] || '',
            available: values[dataStart + 6] || '',
            prices,
        };
    });
    return { lists, rows };
}

/** Serialize list codes + rows back to a price-lists.csv (Action column, fixed columns, one per list). */
export function generatePriceCsv(lists: string[], rows: PriceGridRow[]): string {
    const header = ['Action', 'ServiceType', 'Category', 'ServiceCode', 'Department', 'Name', 'BillableType', 'Available', ...lists];
    const body = rows.map(row => [
        row.action || '',
        row.serviceType, row.category, row.serviceCode, row.department,
        escapeCsvField(row.name), row.billableType, row.available,
        ...lists.map(code => row.prices[code] ?? ''),
    ].join(','));
    return [header.join(','), ...body].join('\n');
}
